import { DatePipe } from "@angular/common";
import { HttpParams } from "@angular/common/http";
import { Component, inject, OnDestroy } from "@angular/core";
import { RouterLink } from "@angular/router";
import { NgAsModule } from "ng-as";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { CheckboxModule } from "primeng/checkbox";
import { DialogModule } from "primeng/dialog";
import { DialogService, DynamicDialogRef } from "primeng/dynamicdialog";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { TableModule } from "primeng/table";
import { TooltipModule } from "primeng/tooltip";
import { Subject, takeUntil } from "rxjs";
import { AbstractPaginableComponent } from "src/app/common/components/paginable/paginable.component";
import { AccountService } from "src/app/common/services/account.service";
import { AclService } from "src/app/common/services/acl.service";
import { MediaStatusDataService } from "src/app/common/services/media-status-data.service";
import { refreshInterval } from "src/app/config";
import { DocumentsSpace } from "src/app/modules/documents-space/documents-space.model";
import { DocumentsSpaceService } from "src/app/modules/documents-space/documents-space.service";
import { DocumentsSpaceSettingsComponent } from "src/app/modules/documents-space/documents-space-settings/documents-space-settings.component";

@Component({
  selector: "app-documents-space-list",
  standalone: true,
  imports: [
    ButtonModule,
    CheckboxModule,
    TooltipModule,
    TableModule,
    ProgressSpinnerModule,
    CardModule,
    DialogModule,
    NgAsModule,
    DatePipe,
    RouterLink,
  ],
  templateUrl: "./documents-space-list.component.html",
  styleUrl: "./documents-space-list.component.scss",
})
export class DocumentsSpaceListComponent extends AbstractPaginableComponent implements OnDestroy {
  documentsSpaces: DocumentsSpace[] = [{} as DocumentsSpace];
  shareLink = `${window.location.href}/`;
  private readonly destroySubject = new Subject<void>();
  private timeout?: NodeJS.Timeout;
  protected readonly aclService = inject(AclService);
  protected readonly accountService = inject(AccountService);
  private readonly documentsSpaceService = inject(DocumentsSpaceService);
  protected readonly DocumentsSpace!: DocumentsSpace;
  private readonly dialogService = inject(DialogService);
  protected readonly mediaStatusData = inject(MediaStatusDataService).mediaStatusData;
  showDisclaimer = localStorage.getItem("documentSpaceDisclaimer") !== "true";

  documentsSpaceSettingsRef?: DynamicDialogRef;

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
    clearTimeout(this.timeout);
  }

  override async loadItems(firstItemIndex: number): Promise<void> {
    clearTimeout(this.timeout);
    this.documentsSpaces = [{} as DocumentsSpace];
    this.pageLoading = true;
    const params = new HttpParams().appendAll({
      page: Number(firstItemIndex / this.pageSize),
      pageSize: this.pageSize,
    });
    const pagedDocumentsSpaces = await this.documentsSpaceService.fetchPaged(params);

    if (pagedDocumentsSpaces) {
      this.pageLoading = false;
      this.documentsSpaces = pagedDocumentsSpaces.elements ?? [];
      this.totalRecords = pagedDocumentsSpaces.totalElements;
      if (this.documentsSpaces.some((doc) => doc.status !== "DONE" && doc.status !== "ERROR")) {
        this.timeout = setTimeout(() => void this.loadItems(firstItemIndex), refreshInterval);
      }
    }
  }

  async deleteItem(id: string): Promise<void> {
    await this.documentsSpaceService.delete(id);
    await this.loadItems(0);
  }

  openDocumentsSpaceSetting(documentsSpaceId?: string): void {
    const header = documentsSpaceId
      ? $localize`:@@documents_space_settings_edit:Modifier l'espace`
      : $localize`:@@documents_space_settings_create:Créer un espace de partage`;
    this.documentsSpaceSettingsRef = this.dialogService.open(DocumentsSpaceSettingsComponent, {
      header,
      width: "70%",
      data: {
        documentsSpaceId,
      },
    });
    this.documentsSpaceSettingsRef.onClose.pipe(takeUntil(this.destroySubject)).subscribe((data) => {
      if (data) {
        void this.loadItems(0);
      }
    });
  }
  onCloseDisclaimer(dontShowAgainDisclaimer: boolean): void {
    if (dontShowAgainDisclaimer) {
      localStorage.setItem("documentSpaceDisclaimer", "true");
    }
    this.showDisclaimer = false;
  }
}
